import type { AdminCapability, AuthenticatedUser, UserRole } from '@services/auth';

// Search index for pages and sections across the application
// This allows users to search for content within pages like Dashboard, Settings, etc.

export type PageSearchResult = {
	type: 'page' | 'section';
	title: string;
	description?: string;
	url: string;
	sectionId?: string; // For highlighting specific sections
	icon: string;
	keywords: string[]; // Searchable keywords
	roles: UserRole[]; // Who can see this
	adminCapability?: AdminCapability;
};

export const pageSearchIndex: PageSearchResult[] = [
	// Dashboard sections
	{
		type: 'page',
		title: 'Dashboard',
		description: 'Overview of your projects, tasks, and activity',
		url: '/app/dashboard',
		icon: 'bi-speedometer2',
		keywords: ['dashboard', 'overview', 'home', 'main'],
		roles: ['admin', 'company', 'gardener', 'seeker']
	},
	{
		type: 'section',
		title: 'Projects in progress',
		description: 'View active projects',
		url: '/app/dashboard',
		sectionId: 'projects-in-progress',
		icon: 'bi-kanban',
		keywords: ['projects', 'in progress', 'active projects', 'jobs'],
		roles: ['admin', 'company', 'gardener', 'seeker']
	},
	{
		type: 'section',
		title: 'Completed projects',
		description: 'View completed projects',
		url: '/app/dashboard',
		sectionId: 'completed-projects',
		icon: 'bi-patch-check-fill',
		keywords: ['completed', 'finished', 'done', 'projects'],
		roles: ['admin', 'company', 'gardener', 'seeker']
	},
	{
		type: 'section',
		title: 'Tasks due soon',
		description: 'Tasks that need attention',
		url: '/app/dashboard',
		sectionId: 'tasks-due-soon',
		icon: 'bi-alarm',
		keywords: ['tasks', 'due', 'soon', 'upcoming', 'reminders'],
		roles: ['admin', 'company', 'gardener']
	},
	{
		type: 'section',
		title: 'Supply vs demand',
		description: 'Marketplace supply and demand metrics',
		url: '/app/dashboard',
		sectionId: 'supply-demand',
		icon: 'bi-people-fill',
		keywords: ['supply', 'demand', 'marketplace', 'ratio', 'metrics'],
		roles: ['admin']
	},
	{
		type: 'section',
		title: 'Offers pending',
		description: 'Pending offers waiting for response',
		url: '/app/dashboard',
		sectionId: 'offers-pending',
		icon: 'bi-envelope-open',
		keywords: ['offers', 'pending', 'quotes', 'responses'],
		roles: ['admin', 'seeker']
	},
	{
		type: 'section',
		title: 'Portfolio projects',
		description: 'All projects in your portfolio',
		url: '/app/dashboard',
		sectionId: 'portfolio-projects',
		icon: 'bi-briefcase',
		keywords: ['portfolio', 'projects', 'all projects', 'company'],
		roles: ['admin', 'company']
	},
	{
		type: 'section',
		title: 'Jobs in progress',
		description: 'Active gardening jobs',
		url: '/app/dashboard',
		sectionId: 'jobs-in-progress',
		icon: 'bi-tools',
		keywords: ['jobs', 'in progress', 'active', 'gardening'],
		roles: ['admin', 'gardener']
	},
	{
		type: 'section',
		title: 'Planning & quotes',
		description: 'Projects in planning phase',
		url: '/app/dashboard',
		sectionId: 'planning-quotes',
		icon: 'bi-clipboard-check',
		keywords: ['planning', 'quotes', 'pipeline', 'waiting'],
		roles: ['admin', 'seeker']
	},
	{
		type: 'section',
		title: 'Total projects',
		description: 'All projects overview',
		url: '/app/dashboard',
		sectionId: 'total-projects',
		icon: 'bi-collection',
		keywords: ['total', 'projects', 'all', 'overview'],
		roles: ['admin', 'company', 'gardener']
	},
	{
		type: 'section',
		title: 'Completed jobs',
		description: 'Finished gardening jobs',
		url: '/app/dashboard',
		sectionId: 'completed-jobs',
		icon: 'bi-check-circle-fill',
		keywords: ['completed', 'jobs', 'finished', 'done'],
		roles: ['admin', 'gardener']
	},

	{
		type: 'page',
		title: 'Admin Errors',
		description: 'Plan creation failures for debugging',
		url: '/app/admin/errors',
		icon: 'bi-bug',
		keywords: ['errors', 'debug', 'plan generation', 'failures', 'admin'],
		roles: ['admin'],
		adminCapability: 'admin_errors'
	},

	{
		type: 'page',
		title: 'Admin Analytics',
		description: 'Deep analytics and insights',
		url: '/app/admin/analytics',
		icon: 'bi-bar-chart-line',
		keywords: ['analytics', 'insights', 'admin', 'metrics', 'statistics'],
		roles: ['admin'],
		adminCapability: 'admin_analytics'
	},
	{
		type: 'section',
		title: 'Supply vs demand',
		description: 'Marketplace supply and demand ratio',
		url: '/app/admin/analytics',
		sectionId: 'supply-demand',
		icon: 'bi-people-fill',
		keywords: ['supply', 'demand', 'marketplace', 'ratio', 'offers per plan'],
		roles: ['admin'],
		adminCapability: 'admin_analytics'
	},
	{
		type: 'section',
		title: 'Plans funnel',
		description: 'Active and draft plans breakdown',
		url: '/app/admin/analytics',
		sectionId: 'plans-funnel',
		icon: 'bi-diagram-3',
		keywords: ['plans', 'funnel', 'active', 'draft', 'breakdown'],
		roles: ['admin'],
		adminCapability: 'admin_analytics'
	},
	{
		type: 'section',
		title: 'Average accepted quote',
		description: 'Average value of accepted offers',
		url: '/app/admin/analytics',
		sectionId: 'average-quote',
		icon: 'bi-currency-euro',
		keywords: ['average', 'quote', 'accepted', 'value', 'price'],
		roles: ['admin'],
		adminCapability: 'admin_analytics'
	},
	{
		type: 'section',
		title: 'Pipeline value',
		description: 'Total value of open projects',
		url: '/app/admin/analytics',
		sectionId: 'pipeline-value',
		icon: 'bi-briefcase',
		keywords: ['pipeline', 'value', 'budget', 'open projects'],
		roles: ['admin'],
		adminCapability: 'admin_analytics'
	},
	{
		type: 'section',
		title: 'Marketplace health analysis',
		description: 'Overall marketplace metrics',
		url: '/app/admin/analytics',
		sectionId: 'marketplace-health',
		icon: 'bi-graph-up',
		keywords: ['marketplace', 'health', 'analysis', 'metrics'],
		roles: ['admin'],
		adminCapability: 'admin_analytics'
	},

	// Settings page
	{
		type: 'page',
		title: 'Settings',
		description: 'Account settings and preferences',
		url: '/app/settings',
		icon: 'bi-gear',
		keywords: ['settings', 'preferences', 'account', 'configuration'],
		roles: ['admin', 'company', 'gardener', 'seeker']
	},
	{
		type: 'section',
		title: 'Account',
		description: 'Account information and details',
		url: '/app/settings',
		sectionId: 'account',
		icon: 'bi-person',
		keywords: ['account', 'profile', 'information', 'details', 'name', 'email'],
		roles: ['admin', 'company', 'gardener', 'seeker']
	},
	{
		type: 'section',
		title: 'Preferences',
		description: 'User preferences and notifications',
		url: '/app/settings',
		sectionId: 'preferences',
		icon: 'bi-sliders',
		keywords: ['preferences', 'notifications', 'email', 'sms', 'dark mode'],
		roles: ['admin', 'company', 'gardener', 'seeker']
	},
	{
		type: 'section',
		title: 'Email notifications',
		description: 'Configure email notification settings',
		url: '/app/settings',
		sectionId: 'email-notifications',
		icon: 'bi-envelope',
		keywords: ['email', 'notifications', 'alerts', 'updates'],
		roles: ['admin', 'company', 'gardener', 'seeker']
	},
	{
		type: 'section',
		title: 'SMS alerts',
		description: 'Configure SMS notification settings',
		url: '/app/settings',
		sectionId: 'sms-alerts',
		icon: 'bi-phone',
		keywords: ['sms', 'alerts', 'phone', 'text', 'notifications'],
		roles: ['admin', 'company', 'gardener', 'seeker']
	},
	{
		type: 'section',
		title: 'Dark mode',
		description: 'Toggle dark mode theme',
		url: '/app/settings',
		sectionId: 'dark-mode',
		icon: 'bi-moon',
		keywords: ['dark', 'mode', 'theme', 'appearance', 'light'],
		roles: ['admin', 'company', 'gardener', 'seeker']
	},

	// Other pages
	{
		type: 'page',
		title: 'Monitoring',
		description: 'Environmental sensor monitoring and readings',
		url: '/app/analytics',
		icon: 'bi-graph-up',
		keywords: ['monitoring', 'sensors', 'environmental', 'readings', 'temperature', 'humidity', 'moisture'],
		roles: ['admin', 'company']
	},
	{
		type: 'page',
		title: 'Profile',
		description: 'Your user profile',
		url: '/app/profile',
		icon: 'bi-person-circle',
		keywords: ['profile', 'user', 'account', 'information'],
		roles: ['admin', 'company', 'gardener', 'seeker']
	}
];

